import {
    AppBar,
    Box,
    Button,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {Link as RouterLink} from "@tanstack/react-router";
import {IdCard, Menu as MenuIcon, UserPlus, X} from "lucide-react";
import React, {useEffect, useMemo, useState} from "react";
import LogoImage from "@/images/Glenbrook-HS_RGB_rev.svg";

type Props = {
    mode?: "instructor";
};

export const LoggedOutHeader: React.FC<Props> = ({mode}) => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const menuItems = useMemo(() => {
        const tempItems = [];

        if (mode === "instructor") {
            tempItems.push({
                icon: <UserPlus size={22} />,
                label: "Create Community Member Account",
                path: "/register-community-member",
                addDivider: true,
            });
            tempItems.push({
                icon: <IdCard size={22} />,
                label: "Instructor / Faculty Login",
                path: "/instructor/sign-in",
                addDivider: false,
            });
        }

        return tempItems;
    }, []);

    useEffect(() => {
        if (!isMobile) {
            setMobileMenuOpen(false);
        }
    }, [isMobile]);

    const renderMobileMenu = () => (
        <Drawer
            anchor="right"
            open={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
            PaperProps={{
                sx: {
                    width: "80%",
                    maxWidth: 360,
                    bgcolor: "primary.main",
                    color: "white",
                },
            }}
        >
            <Box sx={{p: 2, display: "flex", justifyContent: "flex-end"}}>
                <IconButton
                    aria-label="Close menu"
                    onClick={() => setMobileMenuOpen(false)}
                    sx={{color: "white"}}
                >
                    <X size={24} />
                </IconButton>
            </Box>
            <List>
                {menuItems.map((item, index) => (
                    <React.Fragment key={item.path}>
                        <ListItem
                            component={RouterLink}
                            to={item.path}
                            onClick={() => setMobileMenuOpen(false)}
                            sx={{
                                py: 2,
                                "&:hover": {
                                    bgcolor: "rgba(255, 255, 255, 0.1)",
                                },
                            }}
                        >
                            <ListItemIcon sx={{color: "white", minWidth: 40}}>
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{
                                    sx: {fontWeight: 500, color: "white"},
                                }}
                            />
                        </ListItem>
                        {index < menuItems.length - 1 && (
                            <Divider sx={{bgcolor: "rgba(255, 255, 255, 0.2)"}} />
                        )}
                    </React.Fragment>
                ))}
            </List>
        </Drawer>
    );

    return (
        <>
            <AppBar position="static" color={"primary"}>
                <Toolbar sx={{justifyContent: "space-between"}}>
                    <RouterLink
                        to="/"
                        style={{
                            textDecoration: "none",
                            color: "inherit",
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <Box
                            component="img"
                            src={LogoImage}
                            alt="Glenbrook High School"
                            sx={{
                                width: isMobile ? "100px" : "130px",
                                height: "auto",
                                margin: "5px",
                            }}
                        />
                    </RouterLink>

                    {isMobile ? (
                        <Box sx={{display: "flex", gap: 2}}>
                            <IconButton
                                aria-label="Open menu"
                                onClick={() => setMobileMenuOpen(true)}
                                sx={{color: "white"}}
                            >
                                <MenuIcon size={24} />
                            </IconButton>
                        </Box>
                    ) : (
                        <Box sx={{display: "flex", gap: 1.5, alignItems: "center"}}>
                            {menuItems.map(item => (
                                <React.Fragment key={item.path}>
                                    <Button
                                        component={RouterLink}
                                        to={item.path}
                                        sx={{
                                            color: "inherit",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            gap: 1,
                                            minWidth: "auto",
                                            px: 1,
                                        }}
                                    >
                                        {item.icon}
                                        <Typography
                                            variant="caption"
                                            sx={{lineHeight: 1, fontSize: "0.88rem"}}
                                        >
                                            {item.label}
                                        </Typography>
                                    </Button>
                                    {item.addDivider ? (
                                        <Divider
                                            orientation="vertical"
                                            flexItem
                                            sx={{
                                                bgcolor: "rgba(255, 255, 255, 0.7)",
                                                mx: 0.5,
                                                width: "2px",
                                            }}
                                        />
                                    ) : null}
                                </React.Fragment>
                            ))}
                        </Box>
                    )}
                </Toolbar>
                {renderMobileMenu()}
            </AppBar>
        </>
    );
};
